import { axiosInstance } from './api';

export interface WeeklyRow {
  label: string;
  this_week: number | null;
  prev_week: number | null;
  delta_pct: number | null;
}

export interface WeeklySection {
  this_week: number | null;
  prev_week: number | null;
  delta_pct: number | null;
  higher_is_better: boolean;
  configured: boolean;
  // Per-type breakdown (layout: by Rack.rack_type; queue: by CheckoutZone.name).
  rows?: WeeklyRow[];
}

export interface WeeklyReportSections {
  layout: WeeklySection;
  queue: WeeklySection;
  tasks: WeeklySection;
  tables: WeeklySection;
}

export interface WeeklyReportPhoto {
  image_url: string;
  caption: string;
  at?: string | null;
  rack_name?: string;
  fill_pct?: number | null;
  percentile?: number;
  hour?: number;
  captured_occupancy?: number | null;
  /** layout_drops only: 0-based episode index + the episode's length. */
  drop?: number;
  drop_minutes?: number;
}

export interface WeeklyReportPhotoHighlights {
  /** Per-rack percentile trio (25/50/75) since 2026-07-04; before that a
   *  p25 + lowest-hour pair. */
  layout?: WeeklyReportPhoto[];
  /** The week's longest low-fill episodes, ~3 frames each (Начало/Середина/
   *  Конец), grouped by the `drop` index. Reports before 2026-07-04 lack it. */
  layout_drops?: WeeklyReportPhoto[];
  queue?: WeeklyReportPhoto[];
}

export interface WeeklyReportDto {
  id: number;
  zone_id: number;
  period_week: string;
  sections: Partial<WeeklyReportSections>;
  generated_at: string;
  /** Text insights (HTML) — percentile observations, hour-of-day extremes.
   *  Empty string when nothing notable was computed for the week. */
  body_html?: string;
  /** Same shape as DailyReportDto.photo_highlights. */
  photo_highlights?: WeeklyReportPhotoHighlights | null;
}

export interface WeeklyReportPage {
  count: number;
  num_pages: number;
  page: number;
  results: WeeklyReportDto[];
}

export async function getWeeklyReports(
  zoneId: number | string,
  from?: string,
  to?: string,
  page = 1,
): Promise<WeeklyReportPage> {
  const params: Record<string, string | number> = { zone_id: zoneId, page };
  if (from) params.from = from;
  if (to) params.to = to;
  const res = await axiosInstance.get<WeeklyReportPage>('/core/api/weekly-reports/', { params });
  return res.data;
}
